import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '../MainLayout';
import { useSession } from '../../contexts/SessionContext';
import { supabase } from '../../lib/supabase_client';
import { Navigate, useNavigate } from 'react-router-dom';
import '../../styles/page-base.css';

interface MonthlyEntry {
  month: string;
  year: string;
  monthYear: string;
  amount: number;
}

interface SalesData {
  todayCount: number;
  todayAmount: number;
  monthCount: number;
  monthAmount: number;
  /** Revenue from current FY start (1 May) through today */
  currentYeRevenue: number;
  fyStart: string; // ISO date YYYY-MM-DD
  fyEnd: string;   // ISO date YYYY-MM-DD (30 Apr)
  totalRevenue: number;
  monthlyData: MonthlyEntry[];
  comparativeMonthlyData: MonthlyEntry[];
  dailyData: Array<{ 
      date: string;
      fullDate?: string;
      amount: number;
  }>;
}

type TooltipState = {
    x: number;
    y: number;
    text: string;
} | null;

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Current calendar year/month in Asia/Kuala_Lumpur. */
function getMalaysiaNow(): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, month };
}

function buildYearOptions(currentYear: number): number[] {
  const start = 2024;
  const end = Math.max(currentYear, start);
  const years: number[] = [];
  for (let y = end; y >= start; y--) years.push(y);
  return years;
}

export default function AdminSalesAnalytics() {
    const { isAdmin, isSuperadmin, loading: sessionLoading } = useSession();
  const myNow = getMalaysiaNow();
  const [salesData, setSalesData] = useState<SalesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [monthlyTooltip, setMonthlyTooltip] = useState<TooltipState>(null);
  const [dailyTooltip, setDailyTooltip] = useState<TooltipState>(null);
  const [selectedCountry, setSelectedCountry] = useState<'MY' | 'SG'>('MY');
  const [countries, setCountries] = useState<{iso: string, name: string}[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showComparative, setShowComparative] = useState(false);
  const [monthlyYear, setMonthlyYear] = useState(myNow.year);
  const [dailyYear, setDailyYear] = useState(myNow.year);
  const [dailyMonth, setDailyMonth] = useState(myNow.month); // 1..12
  const navigate = useNavigate();
  const yearOptions = buildYearOptions(myNow.year);
  const fetchSalesData = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_sales_analytics', {
        p_country_iso: selectedCountry,
        p_monthly_year: monthlyYear,
        p_daily_year: dailyYear,
        p_daily_month: dailyMonth,
      });

      if (error) {
        console.error('Error fetching sales analytics:', error);
        throw error;
      }

      if (!data) {
        setSalesData(null);
        return;
      }

      const today = data.today || { count: 0, amount_myr: 0, amount_sgd: 0 };
      const thisMonth = data.this_month || { count: 0, amount_myr: 0, amount_sgd: 0 };
      
      const todayAmount = selectedCountry === 'MY' 
        ? Number(today.amount_myr) || 0
        : Number(today.amount_sgd) || 0;
      const monthAmount = selectedCountry === 'MY'
        ? Number(thisMonth.amount_myr) || 0
        : Number(thisMonth.amount_sgd) || 0;
      const totalRevenue = selectedCountry === 'MY'
        ? Number(data.total_revenue_myr) || 0
        : Number(data.total_revenue_sgd) || 0;
      const currentYeRevenue = selectedCountry === 'MY'
        ? Number(data.current_ye_revenue_myr) || 0
        : Number(data.current_ye_revenue_sgd) || 0;
      
      const parseMonthlyEntry = (item: any): MonthlyEntry => ({
        month: item.month,
        year: item.year,
        monthYear: item.month_year,
        amount: Number(item.amount) || 0
      });

      const monthlyData = (data.monthly_data || []).map(parseMonthlyEntry);
      const comparativeMonthlyData = (data.comparative_monthly_data || []).map(parseMonthlyEntry);
      
      const dailyData = (data.daily_data || []).map((item: any) => ({
        date: item.date,
        fullDate: item.full_date,
        amount: Number(item.amount) || 0
      }));

      setSalesData({
        todayCount: today.count || 0,
        todayAmount,
        monthCount: thisMonth.count || 0,
        monthAmount,
        currentYeRevenue,
        fyStart: data.fy_start || '',
        fyEnd: data.fy_end || '',
        totalRevenue,
        monthlyData,
        comparativeMonthlyData,
        dailyData
      });
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedCountry, monthlyYear, dailyYear, dailyMonth]);

  const fetchCountries = useCallback(async () => {
    try {
      setLoadingCountries(true);
      const { data, error } = await supabase
        .from('countries')
        .select('iso, name')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching countries:', error);
        return;
      }

      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionLoading && isAdmin) {
      fetchSalesData();
      fetchCountries();
    }
  }, [sessionLoading, isAdmin, fetchSalesData, fetchCountries]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSalesData();
  };

  const handleUsageAnalytics = () => {
    navigate('/admin/usage-analytics');
  };

  // Show loading while checking admin status
  if (sessionLoading || loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/signin" replace />;
  }

  if (!salesData) {
    return (
      <MainLayout>
        <div className="p-6">
          <p className="text-gray-600">No sales data available</p>
        </div>
      </MainLayout>
    );
  }

  // Format currency for display
  const formatCurrency = (amount: number, currency?: 'MYR' | 'SGD') => {
    const curr = currency || (selectedCountry === 'SG' ? 'SGD' : 'MYR');
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format number without currency symbol (for Y-axis)
  const formatNumber = (amount: number) => {
    if (amount >= 1000) {
      return `${Math.round(amount / 1000)}k`;
    }
    return amount.toString();
  };

  // e.g. "1 May 2026 – 30 Apr 2027"
  const formatFyRange = (startIso: string, endIso: string) => {
    if (!startIso || !endIso) return '';
    const fmt = (iso: string) => {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    return `${fmt(startIso)} – ${fmt(endIso)}`;
  };

  // Calculate nice round maximum for Y-axis based on highest value
  // Calculate nice round maximum for Y-axis based on highest value and currency
  const calculateRoundMax = (maxValue: number, isMonthly: boolean = true) => {
    const isSGD = selectedCountry === 'SG';
    
    if (maxValue === 0) {
      if (isMonthly) {
        return isSGD ? 30000 : 100000;  // SGD: 30k, MYR: 100k
      } else {
        return isSGD ? 3000 : 10000;    // SGD: 3k, MYR: 10k
      }
    }
    
    if (isMonthly) {
      if (isSGD) {
        // For SGD monthly: round up to 10k, 20k, 30k, 40k, 50k, 60k, 70k, 80k, 90k, 100k, etc.
        if (maxValue <= 10000) return 10000;
        if (maxValue <= 20000) return 20000;
        if (maxValue <= 30000) return 30000;
        if (maxValue <= 40000) return 40000;
        if (maxValue <= 50000) return 50000;
        if (maxValue <= 60000) return 60000;
        if (maxValue <= 70000) return 70000;
        if (maxValue <= 80000) return 80000;
        if (maxValue <= 90000) return 90000;
        if (maxValue <= 100000) return 100000;
        // For larger values, round to nearest 10k
        return Math.ceil(maxValue / 10000) * 10000;
      } else {
        // For MYR monthly: original values (100k, 200k, 300k, etc.)
        if (maxValue <= 100000) return 100000;
        if (maxValue <= 200000) return 200000;
        if (maxValue <= 300000) return 300000;
        if (maxValue <= 400000) return 400000;
        if (maxValue <= 500000) return 500000;
        if (maxValue <= 600000) return 600000;
        if (maxValue <= 700000) return 700000;
        if (maxValue <= 800000) return 800000;
        if (maxValue <= 900000) return 900000;
        if (maxValue <= 1000000) return 1000000;
        // For larger values, round to nearest 500k
        return Math.ceil(maxValue / 50000) * 50000;
      }
    } else {
      // Daily chart
      if (isSGD) {
        // For SGD daily: round up to 1k, 2k, 3k, 4k, 5k, 6k, 7k, 8k, 9k, 10k, etc.
        if (maxValue <= 1000) return 1000;
        if (maxValue <= 2000) return 2000;
        if (maxValue <= 3000) return 3000;
        if (maxValue <= 4000) return 4000;
        if (maxValue <= 5000) return 5000;
        if (maxValue <= 6000) return 6000;
        if (maxValue <= 7000) return 7000;
        if (maxValue <= 8000) return 8000;
        if (maxValue <= 9000) return 9000;
        if (maxValue <= 10000) return 10000;
        // For larger values, round to nearest 1k
        return Math.ceil(maxValue / 1000) * 1000;
      } else {
        // For MYR daily: original values (10k, 12k, 14k, etc.)
        if (maxValue <= 10000) return 10000;
        if (maxValue <= 12000) return 12000;
        if (maxValue <= 14000) return 14000;
        if (maxValue <= 16000) return 16000;
        if (maxValue <= 18000) return 18000;
        if (maxValue <= 20000) return 20000;
        if (maxValue <= 25000) return 25000;
        if (maxValue <= 30000) return 30000;
        // For larger values, round to nearest 5k
        return Math.ceil(maxValue / 5000) * 5000;
      }
    }
  };

  const calculateYAxisTicks = (maxValue: number, isMonthly: boolean = true) => {
    const roundMax = calculateRoundMax(maxValue, isMonthly);
    
    // Divide by 10 to get 10 evenly spaced intervals (11 ticks total: 0 to 10)
    const interval = roundMax / 10;
    
    // Generate 11 ticks (0, interval, 2*interval, ..., 10*interval)
    const ticks = [];
    for (let i = 0; i <= 10; i++) {
      ticks.push(i * interval);
    }
    
    return { ticks, max: roundMax };
  };
  
  // Chart dimensions
  const monthlyChartHeight = 300;
  const monthlyChartWidth = 600;
  const dailyChartHeight = 300;
  const dailyChartWidth = 600;

  const maxMonthlyAmount = showComparative
    ? Math.max(
        ...salesData.monthlyData.map(d => d.amount),
        ...salesData.comparativeMonthlyData.map(d => d.amount),
        0
      )
    : Math.max(...salesData.monthlyData.map(d => d.amount), 0);
  const maxDailyAmount = Math.max(...salesData.dailyData.map(d => d.amount), 0);
  
  // Calculate Y-axis with 10 intervals
  const monthlyYAxis = calculateYAxisTicks(maxMonthlyAmount, true);
  const monthlyMax = monthlyYAxis.max;
  const dailyYAxis = calculateYAxisTicks(maxDailyAmount, false);
  const dailyMax = dailyYAxis.max;

  return (
    <MainLayout>
    <div className="page-container">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <p className="text-sm text-gray-500 mb-1">Admin only - Transaction Sales Analytics</p>
            <h1 className="page-title">Sales Analytics Dashboard</h1>
          </div>
          <div className="page-header-right">
            {/* Country Filter */}
            <select 
              value={selectedCountry} 
              onChange={(e) => setSelectedCountry(e.target.value as 'MY' | 'SG')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm mr-3"
              disabled={loadingCountries}
            >
              {countries.filter(c => c.iso === 'MY' || c.iso === 'SG').map(country => (
                <option key={country.iso} value={country.iso}>
                  {country.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="f-btn f-btn-success"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Data</span>
            </button>
            {isSuperadmin ? (
            <button
              onClick={handleUsageAnalytics}
              className="f-btn f-btn-primary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Usage Analytics</span>
            </button>
            ) : null}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Today's Sales */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">{salesData.todayCount}</div>
            <div className="text-sm text-gray-600 mb-1">TRANSACTIONS TODAY</div>
            <div className="text-lg font-semibold text-green-600">{formatCurrency(salesData.todayAmount)}</div>
          </div>

          {/* This Month */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">{salesData.monthCount}</div>
            <div className="text-sm text-gray-600 mb-1">TRANSACTIONS THIS MONTH</div>
            <div className="text-lg font-semibold text-green-600">{formatCurrency(salesData.monthAmount)}</div>
          </div>

          {/* Current YE Revenue (FY starts 1 May, YE 30 Apr) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600 mb-2">{formatCurrency(salesData.currentYeRevenue)}</div>
            <div className="text-sm text-gray-600">CURRENT YEAR REVENUE</div>
            {salesData.fyStart && salesData.fyEnd && (
              <div className="text-xs text-gray-400 mt-1">{formatFyRange(salesData.fyStart, salesData.fyEnd)}</div>
            )}
          </div>

          {/* Total Revenue */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600 mb-2">{formatCurrency(salesData.totalRevenue)}</div>
            <div className="text-sm text-gray-600">ALL-TIME REVENUE</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Sales Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold">Monthly Sales</h2>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowComparative(v => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 select-none"
                  type="button"
                >
                  <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${showComparative ? 'bg-slate-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${showComparative ? 'left-4' : 'left-0.5'}`} />
                  </span>
                  Comparative
                </button>
                {showComparative && (
                  <span className="text-xs text-gray-400">({monthlyYear} vs {monthlyYear - 1})</span>
                )}
                <select
                  value={monthlyYear}
                  onChange={(e) => setMonthlyYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            {showComparative && (
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-3 h-3 rounded-sm"
                    style={{ background: selectedCountry === 'SG' ? '#93c5fd' : '#99dea9' }}
                  />
                  {monthlyYear}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm bg-blue-300" />
                  {monthlyYear - 1}
                </span>
              </div>
            )}
            <div 
              className="relative monthly-chart-container"
              style={{ height: `${monthlyChartHeight + 50}px` }}
            >
            <svg width="100%" height={monthlyChartHeight + 50} viewBox={`0 0 ${monthlyChartWidth} ${monthlyChartHeight + 50}`} preserveAspectRatio="xMidYMid meet">
                {/* RM label at top of Y-axis */}
                <text
                  x="55"
                  y="12"
                  textAnchor="end"
                  className="text-xs fill-gray-700 font-semibold"
                >
                  {selectedCountry === 'SG' ? 'SGD' : 'RM'}
                </text>

                {/* Y-axis labels on TOP (numbers only) */}
                {monthlyYAxis.ticks.map((value, index) => {
                  const ratio = value / monthlyMax;
                  return (
                    <text
                      key={index}
                      x="55"
                      y={20 + (monthlyChartHeight - 60) * (1 - ratio) + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-600 font-medium"
                    >
                      {formatNumber(value)}
                    </text>
                  );
                })}

                {/* Grid lines */}
                {monthlyYAxis.ticks.map((value, index) => {
                  const ratio = value / monthlyMax;
                  return (
                    <line
                      key={index}
                      x1="60"
                      y1={20 + (monthlyChartHeight - 60) * (1 - ratio)}
                      x2={monthlyChartWidth - 20}
                      y2={20 + (monthlyChartHeight - 60) * (1 - ratio)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                  );
                })}

                {/* Bars */}
                {salesData.monthlyData.map((data, index) => {
                  const groupWidth = (monthlyChartWidth - 80) / Math.max(salesData.monthlyData.length, 12);
                  const groupX = 60 + index * groupWidth;
                  const chartBottom = 20 + (monthlyChartHeight - 60);
                  const minHoverHeight = 20;

                  if (showComparative) {
                    const compEntry = salesData.comparativeMonthlyData[index];
                    const singleBar = groupWidth * 0.4;
                    const gap = groupWidth * 0.04;
                    const pairWidth = singleBar * 2 + gap;
                    const pairX = groupX + (groupWidth - pairWidth) / 2;
                    const compX = pairX;
                    const currX = pairX + singleBar + gap;
                    const labelX = groupX + groupWidth / 2;

                    const compAmount = compEntry?.amount ?? 0;
                    const compBarHeight = (compAmount / monthlyMax) * (monthlyChartHeight - 60);
                    const currBarHeight = (data.amount / monthlyMax) * (monthlyChartHeight - 60);

                    const compHoverH = Math.max(compBarHeight, minHoverHeight);
                    const currHoverH = Math.max(currBarHeight, minHoverHeight);

                    return (
                      <g key={index}>
                        {/* Comparative bar (left, blue) */}
                        <rect
                          x={compX}
                          y={chartBottom - compHoverH}
                          width={singleBar}
                          height={compHoverH}
                          fill="transparent"
                          className="cursor-pointer"
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            const c = e.currentTarget.closest('.monthly-chart-container')?.getBoundingClientRect();
                            if (!c) return;
                            setMonthlyTooltip({
                              x: r.left + r.width / 2 - c.left,
                              y: r.top - c.top,
                              text: `${compEntry?.monthYear ?? ''}: ${formatCurrency(compAmount)}`
                            });
                          }}
                          onMouseLeave={() => setMonthlyTooltip(null)}
                        />
                        <rect
                          x={compX}
                          y={chartBottom - compBarHeight}
                          width={singleBar}
                          height={compBarHeight}
                          fill="#93c5fd"
                          rx="3"
                          className="pointer-events-none"
                        />

                        {/* Current bar (right, standard color) */}
                        <rect
                          x={currX}
                          y={chartBottom - currHoverH}
                          width={singleBar}
                          height={currHoverH}
                          fill="transparent"
                          className="cursor-pointer"
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            const c = e.currentTarget.closest('.monthly-chart-container')?.getBoundingClientRect();
                            if (!c) return;
                            setMonthlyTooltip({
                              x: r.left + r.width / 2 - c.left,
                              y: r.top - c.top,
                              text: `${data.monthYear}: ${formatCurrency(data.amount)}`
                            });
                          }}
                          onMouseLeave={() => setMonthlyTooltip(null)}
                        />
                        <rect
                          x={currX}
                          y={chartBottom - currBarHeight}
                          width={singleBar}
                          height={currBarHeight}
                          fill={selectedCountry === 'SG' ? '#93c5fd' : '#99dea9'}
                          rx="3"
                          className="pointer-events-none"
                        />

                        {/* X-axis labels centred on the group */}
                        <text x={labelX} y={monthlyChartHeight - 20} textAnchor="middle" className="text-xs fill-gray-700 font-medium pointer-events-none">
                          {data.month}
                        </text>
                      </g>
                    );
                  }

                  // Single bar (no comparative)
                  const barWidth = groupWidth * 0.8;
                  const x = groupX + groupWidth * 0.1;
                  const barHeight = (data.amount / monthlyMax) * (monthlyChartHeight - 60);
                  const y = chartBottom - barHeight;
                  const hoverHeight = Math.max(barHeight, minHoverHeight);
                  const hoverY = chartBottom - hoverHeight;

                  return (
                    <g key={index}>
                      <rect
                        x={x}
                        y={hoverY}
                        width={barWidth}
                        height={hoverHeight}
                        fill="transparent"
                        className="cursor-pointer"
                        onMouseEnter={(e) => {
                          const barRect = e.currentTarget.getBoundingClientRect();
                          const container = e.currentTarget
                            .closest('.monthly-chart-container')
                            ?.getBoundingClientRect();
                          if (!container) return;
                          setMonthlyTooltip({
                            x: barRect.left + barRect.width / 2 - container.left,
                            y: barRect.top - container.top,
                            text: `${data.monthYear}: ${formatCurrency(data.amount)}`
                          });
                        }}
                        onMouseLeave={() => setMonthlyTooltip(null)}
                      />
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={barHeight}
                        fill={selectedCountry === 'SG' ? "#93c5fd" : "#99dea9"}
                        rx="4"
                        className="pointer-events-none"
                      />
                      <text
                        x={x + barWidth / 2}
                        y={monthlyChartHeight - 20}
                        textAnchor="middle"
                        className="text-xs fill-gray-700 font-medium pointer-events-none"
                      >
                        {data.month}
                      </text>
                    </g>
                  );
                })}
              </svg>
              
              {/* Tooltip for monthly chart */}
              {monthlyTooltip && (
                <div
                  className="absolute bg-gray-900 text-white text-xs rounded px-3 py-2 shadow-lg pointer-events-none z-10"
                  style={{
                    left: monthlyTooltip.x,
                    top: monthlyTooltip.y - 8,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  {monthlyTooltip.text}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>

          {/* Daily Sales Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h2 className="text-lg font-semibold">Daily Sales</h2>
              <div className="flex items-center gap-2">
                <select
                  value={dailyMonth}
                  onChange={(e) => setDailyMonth(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {MONTH_FULL.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={dailyYear}
                  onChange={(e) => setDailyYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-3 text-xs text-gray-400">
              {MONTH_FULL[dailyMonth - 1]} {dailyYear} · {salesData.dailyData.length} days
            </div>
            <div 
              className="relative daily-chart-container"
              style={{ height: `${dailyChartHeight}px` }}
            >
              <svg width="100%" height={dailyChartHeight} viewBox={`0 0 ${dailyChartWidth} ${dailyChartHeight}`} preserveAspectRatio="xMidYMid meet">
                {/* RM label at top of Y-axis */}
                <text
                  x="55"
                  y="12"
                  textAnchor="end"
                  className="text-xs fill-gray-700 font-semibold"
                >
                  {selectedCountry === 'SG' ? 'SGD' : 'RM'}
                </text>

                {/* Y-axis labels on TOP (numbers only) */}
                {dailyYAxis.ticks.map((value, index) => {
                  const ratio = value / dailyMax;
                  return (
                    <text
                      key={index}
                      x="55"
                      y={20 + (dailyChartHeight - 60) * (1 - ratio) + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-600 font-medium"
                    >
                      {formatNumber(value)}
                    </text>
                  );
                })}

                {/* Grid lines */}
                {dailyYAxis.ticks.map((value, index) => {
                  const ratio = value / dailyMax;
                  return (
                    <line
                      key={index}
                      x1="60"
                      y1={20 + (dailyChartHeight - 60) * (1 - ratio)}
                      x2={dailyChartWidth - 20}
                      y2={20 + (dailyChartHeight - 60) * (1 - ratio)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                  );
                })}

                {/* Bars for daily chart */}
                {salesData.dailyData.map((data, index) => {
                  const barWidth = (dailyChartWidth - 80) / Math.max(salesData.dailyData.length, 1);
                  const barHeight = (data.amount / dailyMax) * (dailyChartHeight - 60);
                  const x = 60 + (index * barWidth) + (barWidth * 0.1);
                  const y = 20 + (dailyChartHeight - 60) - barHeight;
                  // Minimum hoverable height (even for zero/small bars)
                  const minHoverHeight = 20;
                  const hoverHeight = Math.max(barHeight, minHoverHeight);
                  const hoverY = 20 + (dailyChartHeight - 60) - hoverHeight;
                  
                  return (
                    <g key={index}>
                      {/* Invisible hover area - always at least minHoverHeight tall */}
                      <rect
                        x={x}
                        y={hoverY}
                        width={barWidth * 0.8}
                        height={hoverHeight}
                        fill="transparent"
                        className="cursor-pointer"
                        onMouseEnter={(e) => {
                          const barRect = e.currentTarget.getBoundingClientRect();
                          const container = e.currentTarget
                            .closest('.daily-chart-container')
                            ?.getBoundingClientRect();
                          
                          if (!container) return;
                          
                          setDailyTooltip({
                            x: barRect.left + barRect.width / 2 - container.left,
                            y: barRect.top - container.top,
                            text: `${data.fullDate || data.date}: ${formatCurrency(data.amount)}`
                          });
                        }}
                        onMouseLeave={() => setDailyTooltip(null)}
                      />
                      {/* Visible bar */}
                      <rect
                        x={x}
                        y={y}
                        width={barWidth * 0.8}
                        height={barHeight}
                        fill="#93c5fd"
                        rx="4"
                        className="pointer-events-none"
                      />
                      <text
                        x={x + (barWidth * 0.4)}
                        y={dailyChartHeight - 20}
                        textAnchor="middle"
                        className="text-[8px] fill-gray-600 pointer-events-none"
                      >
                        {data.date}
                      </text>
                    </g>
                  );
                })}
              </svg>
              
              {/* Tooltip for daily chart */}
              {dailyTooltip && (
                <div
                  className="absolute bg-gray-900 text-white text-xs rounded px-3 py-2 shadow-lg pointer-events-none z-10"
                  style={{
                    left: dailyTooltip.x,
                    top: dailyTooltip.y - 8,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  {dailyTooltip.text}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}